import React, { useEffect, useMemo } from "react";
import { Box, Group, Text, Button, Paper, Divider, Flex } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import useColors from "../../../theme/useColors";
import { useMediaQuery } from "@mantine/hooks";
import { useAppSelector } from "../../../store/hooks";
import {
  getSignatureDateDisplay,
  getSignatureSignerName,
} from "../../../utils/signature-utils";

interface SignatureFooterProps {
  signatureFile: File | string | null;
  signatureInfo?: {
    FIRST_NAME?: string;
    LAST_NAME?: string;
    UPDATED_DATE?: string;
  };
  disableSignButton?: boolean;
  handleSubmit: (val: string | number) => void;
  onSignQuery?: () => void;
  providerName?: string;
  signedValue?: string;
  isPreviewMode?: boolean;
  signInDisabled: boolean;
  hideSaveAndPreview: boolean;
  hideSubmit: boolean;
  submitLoading: boolean;
  saveLoading: boolean;
  timezoneName?: string;
  timezoneAbbrev?: string;
  plainSignerName?: boolean;
  signatureDateFontSize?: string;
}

const SignatureFooter: React.FC<SignatureFooterProps> = ({
  signatureFile,
  signatureInfo,
  disableSignButton,
  handleSubmit,
  onSignQuery,
  signInDisabled,
  hideSaveAndPreview,
  hideSubmit,
  providerName,
  signedValue,
  isPreviewMode = false,
  submitLoading,
  saveLoading,
  timezoneName,
  timezoneAbbrev,
  plainSignerName = false,
  signatureDateFontSize = "12px",
}) => {
  const hasSignature = Boolean(signatureFile);
  const colors = useColors();
  const isMobile = useMediaQuery(`(max-width: ${"622px"})`);
  const provider = useAppSelector((state) => state?.provider?.provider);

  const signaturePreviewUrl = useMemo(() => {
    if (!signatureFile) return null;
    if (typeof signatureFile === "string") return signatureFile;
    if (signatureFile instanceof File) {
      return URL.createObjectURL(signatureFile);
    }
    return null;
  }, [signatureFile]);

  useEffect(() => {
    if (!(signatureFile instanceof File) || !signaturePreviewUrl) return;

    return () => {
      URL.revokeObjectURL(signaturePreviewUrl);
    };
  }, [signatureFile, signaturePreviewUrl]);

  const signatureDateDisplay = getSignatureDateDisplay({
    signatureInfo,
    hideSubmit,
    isPreviewMode,
    timezoneName: timezoneName ?? provider?.timezone_info?.timezone_name,
    timezoneAbbrev: timezoneAbbrev ?? provider?.timezone_info?.abbrev_simple,
  });

  const signerName = getSignatureSignerName(providerName, signatureInfo);

  return (
    <Paper
      px='md'
      py={"4px"}
      withBorder
      radius='sm'
      style={{ borderColor: colors.primary[3] }}
    >
      <Flex justify='space-between' align='center' w='100%' gap={"xs"}>
        <Box>
          <Group align='center' wrap='nowrap' style={{ overflow: "visible" }}>
            {signatureFile ? (
              <Box style={{ overflow: "visible", flexShrink: 0 }}>
                <img
                  src={signaturePreviewUrl ?? undefined}
                  alt='Signature Preview'
                  style={{
                    display: "block",
                    maxWidth: "280px",
                    maxHeight: "72px",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Button
                size={isMobile ? "compact-xs" : "xs"}
                variant='filled'
                color={colors.primary[5]}
                disabled={disableSignButton}
                onClick={onSignQuery}
                styles={{
                  root: {
                    fontWeight: 500,
                    fontSize: isMobile ? "10px" : "12px",
                    padding: "4px 12px",
                    borderRadius: "6px",
                  },
                }}
              >
                <IconPencil size={16} style={{ marginRight: 4 }} />
                Sign Query
              </Button>
            )}

            {(hasSignature || signedValue) && (
              <>
                <Divider
                  style={{ "--divider-color": colors.gray[4] }}
                  my={"10px"}
                  size={"md"}
                  orientation='vertical'
                />

                <Box mb={0}>
                  <Text
                    c='dimmed'
                    style={{ fontSize: "12px", lineHeight: "14px" }}
                  >
                    Electronically Signed by:
                  </Text>
                  <Text
                    pt={"4px"}
                    style={{
                      fontFamily: plainSignerName
                        ? "Roboto, sans-serif"
                        : "'Brush Script MT', cursive",
                      fontSize: plainSignerName ? "16px" : "18px",
                      lineHeight: 1.2,
                      fontWeight: plainSignerName ? 600 : 500,
                    }}
                  >
                    {signerName}
                  </Text>
                </Box>
              </>
            )}

            {signatureDateDisplay.show && (
              <>
                <Divider
                  style={{ "--divider-color": colors.gray[4] }}
                  my={"10px"}
                  size={"md"}
                  orientation='vertical'
                />
                <Text
                  size={signatureDateFontSize}
                  fw={500}
                  ml={"4px"}
                  c={colors.success[5]}
                >
                  {`${signatureDateDisplay.label} ${signatureDateDisplay.text}`}
                </Text>
              </>
            )}
          </Group>
        </Box>

        <Box
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {!hideSaveAndPreview && (
            <Button
              size={isMobile ? "compact-xs" : "xs"}
              variant='outline'
              disabled={signInDisabled || saveLoading || submitLoading}
              onClick={() => handleSubmit(3)}
              loading={saveLoading}
              style={{
                marginRight: isMobile ? 10 : 12,
                fontWeight: 500,
                fontSize: isMobile ? "10px" : "12px",
                "--button-color": colors.gray[8],
              }}
            >
              Save & Preview
            </Button>
          )}

          {!hideSubmit && (
            <Button
              size={isMobile ? "sm" : "md"}
              color={colors.primary[5]}
              disabled={signInDisabled || saveLoading || submitLoading}
              onClick={() => handleSubmit(2)}
              loading={submitLoading}
              styles={{
                root: {
                  fontWeight: 500,
                  fontSize: hideSaveAndPreview
                    ? "12px"
                    : isMobile
                    ? "10px"
                    : "12px",
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "0px",
                  gap: "2px",
                }}
              >
                <div>Submit</div>
                <div>Query</div>
              </div>
            </Button>
          )}
        </Box>
      </Flex>
    </Paper>
  );
};

export default SignatureFooter;
